import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { applyTheme, readTheme } from './lib/theme';
import type { Services } from './services';
import './styles/tokens.css';
import './styles/app.css';

const MOCK = import.meta.env.VITE_API_MOCK === 'true';

async function createServices(): Promise<Services> {
  if (MOCK) {
    // Mock implementations are code-split so production bundles never ship fixtures.
    const [{ MockApiClient }, { MockAuthService }] = await Promise.all([
      import('./api/mock/MockApiClient'),
      import('./auth/mockAuth'),
    ]);
    return { api: new MockApiClient(), auth: new MockAuthService(), mock: true };
  }
  if (import.meta.env.VITE_AUTH_MODE === 'dev') {
    const [{ HttpApiClient }, { DevAuthService }] = await Promise.all([import('./api/http'), import('./auth/devAuth')]);
    const auth = new DevAuthService();
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    return { api: new HttpApiClient(base, () => auth.getIdToken()), auth, mock: false };
  }
  const [{ HttpApiClient }, { AmplifyAuthService, configureAmplify }] = await Promise.all([
    import('./api/http'),
    import('./auth/amplifyAuth'),
  ]);
  // In AWS the API stack writes /config.json next to the app; build-time env vars are the fallback.
  const env = import.meta.env;
  let runtime: { apiUrl?: string; userPoolId?: string; userPoolClientId?: string } = {};
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) runtime = await res.json();
  } catch {
    /* no runtime config: use env */
  }
  const apiUrl = (runtime.apiUrl ?? env.VITE_API_BASE_URL)?.replace(/\/v1\/?$/, '');
  const userPoolId = runtime.userPoolId ?? env.VITE_COGNITO_USER_POOL_ID;
  const clientId = runtime.userPoolClientId ?? env.VITE_COGNITO_CLIENT_ID;
  if (!apiUrl || !userPoolId || !clientId) {
    throw new Error('Missing configuration: API URL and Cognito ids (config.json or VITE_* variables). See web/README.md.');
  }
  configureAmplify({ userPoolId, clientId });
  const auth = new AmplifyAuthService();
  return { api: new HttpApiClient(apiUrl, () => auth.getIdToken()), auth, mock: false };
}

applyTheme(readTheme());
const root = createRoot(document.getElementById('root')!);

createServices()
  .then((services) => {
    root.render(
      <StrictMode>
        <App services={services} />
      </StrictMode>,
    );
  })
  .catch((err: Error) => {
    root.render(
      <div className="auth-page">
        <div className="error-box" role="alert">
          <h3>DemandPilot couldn’t start</h3>
          <p>{err.message}</p>
        </div>
      </div>,
    );
  });
